// src/components/websearch/LeadMessenger.js
import React, { useState, useEffect } from 'react';
import { 
  MessageSquare, X, Plus, Edit, Trash2, Mail, MessageCircle,
  Loader2, Check, AlertCircle, Tag, Type
} from 'lucide-react';
import { websearchService } from '../../services/websearchService';

const MESSAGE_CATEGORIES = [
  'Lead Warm Up',
  'Lead Validation',
  'Follow Up',
  'Meeting Request',
  'Product Demo',
  'Closing',
  'Re-engagement',
  'Custom'
];

export const LeadMessenger = ({ bookId, onClose }) => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingMessage, setEditingMessage] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [formData, setFormData] = useState({
    category: 'Lead Warm Up',
    type: 'whatsapp',
    subject: '',
    content: ''
  });

  useEffect(() => {
    loadMessages();
  }, [bookId]);

  const loadMessages = async () => {
    try {
      setLoading(true);
      const data = await websearchService.getLeadMessages(bookId);
      setMessages(data);
    } catch (error) {
      console.error('Error loading messages:', error);
      setError('Failed to load messages');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.content.trim()) return;

    try {
      if (editingMessage) {
        await websearchService.updateLeadMessage(bookId, editingMessage.id, formData);
        setSuccess('Message updated successfully');
      } else {
        await websearchService.createLeadMessage(bookId, formData);
        setSuccess('Message created successfully');
      }
      
      resetForm();
      await loadMessages();
    } catch (error) {
      console.error('Error saving message:', error);
      setError('Failed to save message');
    }
  };

  const handleEdit = (message) => {
    setEditingMessage(message);
    setFormData({
      category: message.category,
      type: message.type,
      subject: message.subject || '',
      content: message.content
    });
    setShowForm(true);
  };

  const handleDelete = async (messageId) => {
    if (!window.confirm('Delete this message template?')) return;

    try {
      await websearchService.deleteLeadMessage(bookId, messageId);
      setSuccess('Message deleted');
      await loadMessages();
    } catch (error) {
      console.error('Error deleting message:', error);
      setError('Failed to delete message');
    }
  };

  const resetForm = () => {
    setFormData({
      category: 'Lead Warm Up',
      type: 'whatsapp',
      subject: '',
      content: ''
    });
    setEditingMessage(null);
    setShowForm(false);
  };

  const whatsappMessages = messages.filter(m => m.type === 'whatsapp');
  const emailMessages = messages.filter(m => m.type === 'email');

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-slate-900 w-full max-w-4xl h-[90vh] rounded-xl shadow-2xl flex flex-col">
        {/* Header */}
        <div className="border-b border-slate-800 p-6 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-white flex items-center gap-2">
              <MessageSquare className="h-6 w-6" />
              Lead Messages
            </h2>
            <p className="text-slate-400 mt-1">Create reusable message templates</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-800 rounded-lg transition-colors">
            <X className="h-6 w-6 text-slate-400" />
          </button>
        </div>

        {/* Success/Error Messages */}
        {error && (
          <div className="mx-6 mt-4 p-3 bg-red-500/10 border border-red-500/50 rounded-lg flex items-center gap-2 text-red-400">
            <AlertCircle className="h-5 w-5" />
            {error}
          </div>
        )}
        {success && (
          <div className="mx-6 mt-4 p-3 bg-green-500/10 border border-green-500/50 rounded-lg flex items-center gap-2 text-green-400">
            <Check className="h-5 w-5" />
            {success}
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {!showForm ? (
            <>
              <button
                onClick={() => setShowForm(true)}
                className="w-full mb-6 p-4 border-2 border-dashed border-slate-700 hover:border-indigo-500 rounded-lg text-slate-400 hover:text-white transition-colors flex items-center justify-center gap-2"
              >
                <Plus className="h-5 w-5" />
                Create New Message Template
              </button>

              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-indigo-500" />
                </div>
              ) : (
                <div className="space-y-6">
                  {/* WhatsApp Messages */}
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
                      <MessageCircle className="h-5 w-5 text-green-500" />
                      WhatsApp Messages ({whatsappMessages.length})
                    </h3>
                    <div className="space-y-3">
                      {whatsappMessages.map(msg => (
                        <MessageCard
                          key={msg.id}
                          message={msg}
                          onEdit={handleEdit}
                          onDelete={handleDelete}
                        />
                      ))}
                      {whatsappMessages.length === 0 && (
                        <p className="text-slate-500 text-center py-8">No WhatsApp messages yet</p>
                      )}
                    </div>
                  </div>

                  {/* Email Messages */}
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
                      <Mail className="h-5 w-5 text-blue-500" />
                      Email Messages ({emailMessages.length})
                    </h3>
                    <div className="space-y-3">
                      {emailMessages.map(msg => (
                        <MessageCard
                          key={msg.id}
                          message={msg}
                          onEdit={handleEdit}
                          onDelete={handleDelete}
                        />
                      ))}
                      {emailMessages.length === 0 && (
                        <p className="text-slate-500 text-center py-8">No email messages yet</p>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    <Tag className="inline h-4 w-4 mr-1" />
                    Category
                  </label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    {MESSAGE_CATEGORIES.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    <Type className="inline h-4 w-4 mr-1" />
                    Type
                  </label>
                  <select
                    value={formData.type}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="whatsapp">WhatsApp</option>
                    <option value="email">Email</option>
                  </select>
                </div>
              </div>

              {formData.type === 'email' && (
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Email Subject
                  </label>
                  <input
                    type="text"
                    value={formData.subject}
                    onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                    placeholder="Enter email subject..."
                    className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Message Content
                </label>
                <textarea
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  placeholder="Enter your message..."
                  rows={10}
                  className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
                <p className="mt-2 text-sm text-slate-500">
                  Use variables: {'{name}'}, {'{company}'}, {'{email}'}, {'{phone}'}
                </p>
              </div>

              <div className="flex gap-3">
                <button
                  type="submit"
                  className="flex-1 px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-medium transition-colors"
                >
                  {editingMessage ? 'Update Message' : 'Create Message'}
                </button>
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-6 py-3 bg-slate-800 hover:bg-slate-700 text-white rounded-lg font-medium transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

const MessageCard = ({ message, onEdit, onDelete }) => (
  <div className="bg-slate-800 border border-slate-700 rounded-lg p-4">
    <div className="flex items-start justify-between mb-3">
      <div className="flex items-center gap-2">
        <span className="px-3 py-1 bg-indigo-500/20 text-indigo-400 rounded-full text-sm font-medium">
          {message.category}
        </span>
        {message.type === 'email' && message.subject && (
          <span className="text-slate-400 text-sm">• {message.subject}</span>
        )}
      </div>
      <div className="flex gap-2">
        <button
          onClick={() => onEdit(message)}
          className="p-1.5 hover:bg-slate-700 rounded transition-colors"
        >
          <Edit className="h-4 w-4 text-slate-400" />
        </button>
        <button
          onClick={() => onDelete(message.id)}
          className="p-1.5 hover:bg-slate-700 rounded transition-colors"
        >
          <Trash2 className="h-4 w-4 text-red-400" />
        </button>
      </div>
    </div>
    <p className="text-slate-300 text-sm whitespace-pre-wrap">{message.content}</p>
  </div>
);